import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Typography,
  FormControlLabel, Checkbox, FormGroup, Box,
  Autocomplete, Chip
} from '@mui/material';
import { toast } from 'react-toastify';
import { apigetPublic, apiputFormData } from '../../service/api';

const titleOptions = [
  'Mortgage Loan Officer',
  'Branch Manager, Mortgage Loan Officer',
  'Area Manager, Mortgage Loan Officer'
];

/* ---------------------------------------------
   Helpers to FIX legacy “stringified array” data
---------------------------------------------- */
function tryJsonParse(s) {
  if (typeof s !== 'string') return s;
  const t = s.trim();
  if (!t) return s;
  // Only attempt parse when it looks like JSON
  if (!(t.startsWith('[') || t.startsWith('{') || t.startsWith('"'))) return s;
  try {
    return JSON.parse(t);
  } catch {
    return s;
  }
}

// Recursively unwrap arrays that contain JSON-strings of arrays
function unwrapToStringArray(value) {
  const out = [];

  const walk = (v) => {
  if (v == null) {
    return;
  }

  if (typeof v === 'string') {
    const parsed = tryJsonParse(v);
    if (parsed !== v) {
      walk(parsed);
    } else {
      out.push(v);
    }
    return;
  }

  if (Array.isArray(v)) {
    v.forEach(walk);
  }
};


  walk(value);

  return out
    .map(s => String(s).trim())
    .filter(Boolean);
}

function uniqueStrings(arr) {
  return Array.from(new Set((arr || []).map(s => String(s).trim()).filter(Boolean)));
}

const LoanOfficerEditModal = ({ open, onClose, loanOfficer, onUpdate }) => {
  const DEFAULT_BIO =
    `At Jet HomeLoans, we’re dedicated to providing you with top-flight resources and first-class service to help you reach your dreams of homeownership.`;

  const [formData, setFormData] = useState({});
  const [headshotFile, setHeadshotFile] = useState(null);
  const [branchImageFile, setBranchImageFile] = useState(null);

  // Dynamic lists
  const [statesList, setStatesList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);

  // Load states + branches
  useEffect(() => {
    const load = async () => {
      const s = await apigetPublic('states/search');
      if (s?.status === 200) setStatesList(s.data.result || []);

      const b = await apigetPublic('branches/search');
      if (b?.status === 200) setBranchesList(b.data.result || []);
    };
    load();
  }, []);

  // ---------------------------------------------
  // FIX #1: Load LO into state, but UNWRAP arrays
  // ---------------------------------------------
  useEffect(() => {
    if (!loanOfficer) return;

    setFormData({
      ...loanOfficer,
      states: uniqueStrings(unwrapToStringArray(loanOfficer.states)),
      branches: uniqueStrings(unwrapToStringArray(loanOfficer.branches)),
      stateOptionsSelected: uniqueStrings(unwrapToStringArray(loanOfficer.stateOptionsSelected)),
      groups: uniqueStrings(unwrapToStringArray(loanOfficer.groups)),
    });
  }, [loanOfficer]);

  // Handlers
  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleStateCheckboxChange = (e) => {
    const { value, checked } = e.target;
    const current = uniqueStrings(formData.states);

    const updated = checked
      ? uniqueStrings([...current, value])
      : current.filter(x => x !== value);

    setFormData(prev => ({ ...prev, states: updated }));
  };

  const handleStateOptionCheckbox = (option, stateName, checked) => {
    const key = `${stateName}::${option}`;
    const current = uniqueStrings(formData.stateOptionsSelected);

    const updated = checked
      ? uniqueStrings([...current, key])
      : current.filter(x => x !== key);

    setFormData(prev => ({ ...prev, stateOptionsSelected: updated }));
  };

  const handleBranchCheckboxChange = (branchName, checked) => {
    const current = uniqueStrings(formData.branches);

    const updated = checked
      ? uniqueStrings([...current, branchName])
      : current.filter(b => b !== branchName);

    setFormData(prev => ({ ...prev, branches: updated }));
  };

  // ---------------------------------------------
  // FIX #2: Submit arrays as repeated multipart keys
  // (NOT JSON.stringify arrays)
  // ---------------------------------------------
  const handleSubmit = async () => {
    try {
      const data = new FormData();

      const payload = {
        ...formData,
        bio: formData.bio?.trim() || DEFAULT_BIO,

        // ensure these are clean before sending
        states: uniqueStrings(formData.states),
        branches: uniqueStrings(formData.branches),
        stateOptionsSelected: uniqueStrings(formData.stateOptionsSelected),
        groups: uniqueStrings(formData.groups),
      };

      // append primitives
      Object.entries(payload).forEach(([key, val]) => {
        if (Array.isArray(val)) return; // handled below
        data.append(key, val ?? '');
      });

      // append arrays as repeated keys
      const appendArray = (key, arr) => {
        (arr || []).forEach(item => data.append(key, item));
      };

      appendArray('states', payload.states);
      appendArray('branches', payload.branches);
      appendArray('stateOptionsSelected', payload.stateOptionsSelected);
      appendArray('groups', payload.groups);

      if (headshotFile) data.append('headshot', headshotFile);
      if (branchImageFile) data.append('branchImage', branchImageFile);

      const result = await apiputFormData(`loanofficer/${loanOfficer.slug}`, data);

      if (result?.status === 200) {
        toast.success('Loan Officer updated!');
        onUpdate?.();
        onClose?.();
      } else {
        toast.error('Error updating officer');
      }
    } catch (err) {
      toast.error('Unexpected error while updating.');
      console.error(err);
    }
  };

  if (!loanOfficer) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Loan Officer</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* BASIC FIELDS */}
          {['firstName', 'lastName', 'title', 'email', 'phone', 'nmlsId'].map(field => (
            <Grid item xs={12} sm={6} key={field}>
              <TextField
                fullWidth
                name={field}
                label={field.replace(/([A-Z])/g, ' $1')}
                value={formData[field] || ''}
                onChange={handleChange}
              />
            </Grid>
          ))}

          {/* SLUG */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="slug"
              label="Slug"
              value={formData.slug || ''}
              onChange={handleChange}
            />
          </Grid>

          {/* BIO */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              name="bio"
              label="Bio"
              value={formData.bio || ''}
              onChange={handleChange}
            />
          </Grid>

          {/* LINKS */}
          {[
            ['applyLink', 'Apply Now Link'],
            ['googleReviewLink', 'Google Review Link'],
            ['linkedinUrl', 'LinkedIn URL'],
            ['facebook', 'Facebook URL'],
            ['instagram', 'Instagram URL'],
            ['tiktok', 'TikTok URL'],
            ['twitter', 'Twitter URL'],
          ].map(([name, label]) => (
            <Grid item xs={12} sm={6} key={name}>
              <TextField
                fullWidth
                name={name}
                label={label}
                value={formData[name] || ''}
                onChange={handleChange}
              />
            </Grid>
          ))}

          {/* GROUPS */}
          <Grid item xs={12}>
            <Typography variant="h6">Groups</Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.groups || []}
              onChange={(e, newValue) =>
                setFormData(prev => ({ ...prev, groups: uniqueStrings(newValue) }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type a group and press Enter"
                  helperText="Example: veterans, first-time-buyers, investors"
                />
              )}
            />
          </Grid>

          {/* LICENSING FOR GA */}
          {formData.states?.includes('Georgia') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="additionalLicensing"
                label="Additional Licensing (for GA)"
                value={formData.additionalLicensing || ''}
                onChange={handleChange}
              />
            </Grid>
          )}

          {/* IMAGE UPLOAD */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Current Headshot</Typography>
            {formData.imageUrl && (
              <img
                src={formData.imageUrl}
                alt="Headshot"
                style={{
                  width: 120,
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: 8,
                  marginBottom: 10
                }}
              />
            )}

            <Typography variant="subtitle2">Upload New Headshot</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setHeadshotFile(e.target.files?.[0] || null)}
            />
          </Grid>

          {/* STATES */}
          <Grid item xs={12}>
            <Typography variant="h6">States</Typography>
            <FormGroup>
              {statesList.map(state => {
                const isSelected = (formData.states || []).includes(state.name);

                return (
                  <Box key={state._id} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          value={state.name}
                          onChange={handleStateCheckboxChange}
                        />
                      }
                      label={`${state.name} (${state.code})`}
                    />

                    {isSelected && state.additionalOptions?.length > 0 && (
                      <Box sx={{ ml: 5 }}>
                        {state.additionalOptions.map(option => {
                          const key = `${state.name}::${option}`;
                          const isChecked = (formData.stateOptionsSelected || []).includes(key);

                          return (
                            <FormControlLabel
                              key={option}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) =>
                                    handleStateOptionCheckbox(option, state.name, e.target.checked)
                                  }
                                />
                              }
                              label={option}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </FormGroup>
          </Grid>

          {/* BRANCHES */}
          <Grid item xs={12}>
            <Typography variant="h6">Branches</Typography>
            <FormGroup>
              {branchesList.map(branch => {
                const isChecked = (formData.branches || []).includes(branch.name);

                return (
                  <FormControlLabel
                    key={branch._id}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => handleBranchCheckboxChange(branch.name, e.target.checked)}
                      />
                    }
                    label={`${branch.name} — ${branch.city}, ${branch.stateCode}`}
                  />
                );
              })}
            </FormGroup>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>Save Changes</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanOfficerEditModal;
